import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Grid from '@mui/material/Grid2';

import { FloatingFeature } from "@models";
import { DATA_TYPE_INFO, NON_PLAYER_DATA_TYPES } from "@dndModels/Constants";
import { DataType, SourceBookType } from '@dndModels/Types';

import API from "@utilities/smart_api";
import { buildFeatureAliases, buildFeatureSourceBooks, buildFeatureUsageIndex, describeUsagePath, FeatureUsageIndex, UsageSourceObject } from '../../utilities/feature_usage';

import LoadingBox from "@components/input/LoadingBox";
import SelectStringBox from '@components/input/selects/SelectStringBox';
import CheckBox from '@components/input/CheckBox';
import StringBox from '@components/input/strings/StringBox';
import DandDButtonBox from '@dndComponents/DandDButtonBoxSimple';

const api = API.getInstance();

const FEATURE_TYPES: DataType[] = ["floating_feature", "creature_feature"];
// Every content collection is scanned - the walker is generic, so types that
// never reference features just contribute nothing. Player and campaign objects
// (characters etc.) are deliberately not counted as uses.
// standard_features isn't in NON_PLAYER_DATA_TYPES but holds features directly;
// logs and support tickets never reference features and can be large.
const SCAN_TYPES: DataType[] = Array.from(new Set<DataType>([...NON_PLAYER_DATA_TYPES, "standard_features"]))
  .filter(data_type => data_type !== "error_log" && data_type !== "ticket");
const PAGE_SIZE = 100;
const LIGHT_CHUNK_SIZE = 200;

interface ScanResult { data_type: DataType, expected: number, loaded: number, error: string }

// Every document in a collection, straight from the database: no model classes,
// no smart_hash, and no blocked/archived/source book filtering - so nothing the
// app would normally hide (e.g. blocked classes) can make a feature look unused.
const fetchRawCollection = async (data_type: DataType, collection: string = data_type, filter: any = {}) => {
  const count = await api.getObjectCountFromAPIRaw({ collection, filter });
  if (!count || count.count === undefined || count.count < 0) {
    throw new Error(`Couldn't count ${collection}`);
  }
  const docs = count.count > 0 ? await api.getAllObjectsFromAPIRaw({ data_type, collection, filter }) : [];
  return { expected: count.count as number, docs: docs as any[] };
};

// Extended JSON, so a Compass import restores _id as an ObjectId rather than a string.
const downloadAsExtendedJson = (file_name: string, docs: any[]) => {
  const ejson = docs.map(doc => ({ ...doc, _id: { "$oid": doc._id } }));
  const blob = new Blob([JSON.stringify(ejson, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = file_name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
};

const typeName = (data_type: DataType) => DATA_TYPE_INFO.find(o => o.data_type === data_type)?.name ?? data_type;

export default function FeatureUsage() {
  const [loaded_count, setLoadedCount] = useState(0);
  const [scan_results, setScanResults] = useState<ScanResult[]>([]);
  const [show_scan_details, setShowScanDetails] = useState(false);
  const [delete_status, setDeleteStatus] = useState("");
  const [backup_error, setBackupError] = useState("");
  // The raw stored documents of every feature, for the backup taken before deleting.
  const raw_features = useRef(new Map<string, any>());
  const [error, setError] = useState("");
  const [features, setFeatures] = useState<Array<{ data_type: DataType, obj: FloatingFeature }>>([]);
  const [index, setIndex] = useState<FeatureUsageIndex | null>(null);
  const [filter, setFilter] = useState("");
  const [unused_only, setUnusedOnly] = useState(false);
  const [show_creature_features, setShowCreatureFeatures] = useState(true);
  const [limit, setLimit] = useState(PAGE_SIZE);
  const [expanded, setExpanded] = useState<string[]>([]);
  const [confirm_delete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState<{ done: number, total: number, failed: string[] } | null>(null);
  const [feature_books, setFeatureBooks] = useState<Map<string, string[]>>(new Map());
  const [book_names, setBookNames] = useState<Map<string, string>>(new Map());
  const [book_info, setBookInfo] = useState<Map<string, { type: SourceBookType, third_party: boolean }>>(new Map());
  const [book_filter, setBookFilter] = useState<string[]>([]);
  const [needs_update_only, setNeedsUpdateOnly] = useState(false);
  const [no_book_only, setNoBookOnly] = useState(false);
  const [confirm_save, setConfirmSave] = useState(false);
  const [saving, setSaving] = useState<{ done: number, total: number, failed: string[] } | null>(null);
  // Bumped after saves, since they mutate source_books_old in place.
  const [saved_version, setSavedVersion] = useState(0);
  const mounted = useRef(false);

  useEffect(() => {
    mounted.current = true;
    const load = async () => {
      try {
        const scans: ScanResult[] = [];
        const results = await Promise.all(SCAN_TYPES.map(async data_type => {
          let docs: any[] = [];
          try {
            const res = await fetchRawCollection(data_type);
            docs = res.docs;
            scans.push({ data_type, expected: res.expected, loaded: docs.length,
              error: docs.length < res.expected ? `Only got ${docs.length} of ${res.expected}` : "" });
          } catch (ex: any) {
            console.error(ex);
            scans.push({ data_type, expected: 0, loaded: 0, error: ex?.message ?? "Failed to load" });
          }
          if (mounted.current) {
            setLoadedCount(prev => prev + 1);
          }
          return { data_type, objects: docs as UsageSourceObject[] };
        }));
        if (!mounted.current) {
          return;
        }
        raw_features.current = new Map(results
          .filter(r => FEATURE_TYPES.includes(r.data_type))
          .flatMap(r => r.objects.map(doc => [doc._id, doc] as [string, any])));
        const all_features = results
          .filter(r => FEATURE_TYPES.includes(r.data_type))
          .flatMap(r => r.objects.map(doc => ({ data_type: r.data_type, obj: api.new_feature(doc) as FloatingFeature })));
        const aliases = buildFeatureAliases(all_features.map(o => o.obj));
        const new_index = buildFeatureUsageIndex(results, aliases);
        const book_result = results.find(r => r.data_type === "source_book");
        const books = (book_result?.objects ?? []) as unknown as Array<{ _id: string, name: string, abbreviation?: string, source_book_type: SourceBookType, third_party: boolean }>;
        setScanResults(scans.sort((a, b) => a.data_type.localeCompare(b.data_type)));
        const names = new Map<string, string>(books.map(b => [b._id, b.abbreviation ? `${b.name} (${b.abbreviation})` : b.name]));
        names.set("Personal", "Personal Homebrew");
        setBookNames(names);
        setBookInfo(new Map(books.map(b => [b._id, { type: b.source_book_type, third_party: b.third_party }])));
        setFeatureBooks(buildFeatureSourceBooks(new_index, FEATURE_TYPES));
        setIndex(new_index);
        setFeatures(all_features.sort((a, b) => a.obj.name.localeCompare(b.obj.name)));
      } catch (ex: any) {
        console.error(ex);
        if (mounted.current) {
          setError(ex?.message ?? "Failed to load");
        }
      }
    };
    load();
    return () => {
      mounted.current = false;
    };
  }, []);

  // What should be saved on the feature: every book it's used in, minus the
  // "Personal" pseudo-book that's stamped on sourceless homebrew at load.
  const targetBooks = (feature_id: string) =>
    (feature_books.get(feature_id) ?? []).filter(id => id !== "Personal").sort();
  const savedBooks = (obj: FloatingFeature) =>
    obj.source_books_old.map(o => o.id).filter((id, i, arr) => arr.indexOf(id) === i).sort();
  const needsUpdate = (obj: FloatingFeature) => {
    const target = targetBooks(obj._id);
    const saved = savedBooks(obj);
    return target.length > 0 && (target.length !== saved.length || target.some((id, i) => id !== saved[i]));
  };

  const filtered = useMemo(() => {
    if (!index) {
      return [];
    }
    const lower = filter.trim().toLowerCase();
    return features.filter(({ data_type, obj }) => {
      if (!show_creature_features && data_type === "creature_feature") {
        return false;
      }
      if (unused_only && index.usages.has(obj._id)) {
        return false;
      }
      // Used somewhere, but none of its uses lead to a real source book.
      if (no_book_only && !(index.usages.has(obj._id) && targetBooks(obj._id).length === 0)) {
        return false;
      }
      if (needs_update_only && !needsUpdate(obj)) {
        return false;
      }
      if (book_filter.length > 0 && !(feature_books.get(obj._id) ?? []).some(id => book_filter.includes(id))) {
        return false;
      }
      return lower === "" || obj.name.toLowerCase().includes(lower) || obj.feature_type.toLowerCase().includes(lower);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [features, index, filter, unused_only, show_creature_features, book_filter, feature_books, needs_update_only, no_book_only, saved_version]);

  // Only offer books that at least one feature actually appears in.
  const book_options = useMemo(() => {
    const ids = new Set<string>();
    feature_books.forEach(list => list.forEach(id => ids.add(id)));
    return Array.from(ids)
      .map(id => ({ id, text: book_names.get(id) ?? `Unknown (${id})` }))
      .sort((a, b) => a.text.localeCompare(b.text));
  }, [feature_books, book_names]);

  if (!api.user || !api.user.admin) {
    return <div className="centered">Admins only.</div>;
  }
  if (error) {
    return <div className="centered">{ error }</div>;
  }
  if (!index) {
    return (
      <div className="centered">
        <LoadingBox
          message={`Scanning every collection (${loaded_count} / ${SCAN_TYPES.length} types)`}
          percentage={(loaded_count / SCAN_TYPES.length) * 100}
          size={window.innerHeight * 0.5}
          height={window.innerHeight - 81}
        />
      </div>
    );
  }

  const unused_total = features.filter(f => !index.usages.has(f.obj._id)).length;
  // Respects the current filters, so the delete can be scoped by name/type.
  const deletable = filtered.filter(f => !index.usages.has(f.obj._id));
  const delete_running = !!deleting && deleting.done < deleting.total;
  const need_update_total = features.filter(f => needsUpdate(f.obj)).length;
  const unresolvable_total = features.filter(f => index.usages.has(f.obj._id) && targetBooks(f.obj._id).length === 0).length;
  const savable = filtered.filter(f => needsUpdate(f.obj));
  const save_running = !!saving && saving.done < saving.total;
  // Locks the filters while a bulk action is pending or running so its target set can't shift.
  const busy = confirm_delete || delete_running || confirm_save || save_running || delete_status !== "";
  // A collection that failed or came back short could hide uses, so nothing gets
  // deleted or re-sourced until a clean scan.
  const scan_failures = scan_results.filter(o => o.error !== "");
  const scanned_docs = scan_results.reduce((sum, o) => sum + o.loaded, 0);

  const saveSourceBooks = async () => {
    setConfirmSave(false);
    const targets = [...savable];
    const failed: string[] = [];
    setSaving({ done: 0, total: targets.length, failed });
    for (let i = 0; i < targets.length; ++i) {
      const { data_type, obj } = targets[i];
      const source_books = targetBooks(obj._id).map(id => ({
        id,
        type: book_info.get(id)?.type ?? "Source Book" as SourceBookType,
        third_party: book_info.get(id)?.third_party ?? false,
      }));
      try {
        if (!await api.updateSourceBooks(data_type, obj, source_books)) {
          failed.push(obj.name);
        }
      } catch (ex) {
        console.error(ex);
        failed.push(obj.name);
      }
      if (!mounted.current) {
        return;
      }
      setSaving({ done: i + 1, total: targets.length, failed: [...failed] });
    }
    setSavedVersion(prev => prev + 1);
  };

  const renderSave = () => {
    if (saving && save_running) {
      return <>Saving source books { saving.done } / { saving.total }...</>;
    }
    if (confirm_save) {
      return (
        <>
          <div>
            Replace the saved source books on { savable.length } feature{ savable.length === 1 ? "" : "s" } with the books they&apos;re used in?
          </div>
          <DandDButtonBox name={`Yes, Save ${savable.length}`} onClick={saveSourceBooks} />
          <DandDButtonBox name="Cancel" onClick={() => setConfirmSave(false)} />
        </>
      );
    }
    return (
      <>
        <DandDButtonBox name={`Save Source Books (${savable.length})`} disabled={savable.length === 0 || busy || scan_failures.length > 0} onClick={() => setConfirmSave(true)} />
        { saving &&
          <div>
            Saved { saving.total - saving.failed.length } of { saving.total }.
            { saving.failed.length > 0 && ` Failed: ${saving.failed.join(", ")}` }
          </div>
        }
      </>
    );
  };

  // Downloads the full and _light documents of everything about to be deleted,
  // one Extended JSON file per collection, so it can be re-imported with Compass.
  const downloadBackup = async (targets: typeof deletable) => {
    const stamp = new Date().toISOString().replace(/[:.]/g, "-");
    for (const data_type of FEATURE_TYPES) {
      const of_type = targets.filter(t => t.data_type === data_type);
      if (of_type.length === 0) {
        continue;
      }
      setDeleteStatus(`Backing up ${typeName(data_type)} documents...`);
      const full_docs = of_type.map(t => raw_features.current.get(t.obj._id)).filter(doc => !!doc);
      if (full_docs.length !== of_type.length) {
        throw new Error(`Missing stored documents for ${of_type.length - full_docs.length} ${typeName(data_type)}(s)`);
      }
      const light_ids = full_docs.map(doc => doc.light_id).filter((id: any) => typeof id === "string" && id !== "");
      const light_docs: any[] = [];
      for (let i = 0; i < light_ids.length; i += LIGHT_CHUNK_SIZE) {
        const res = await fetchRawCollection(data_type, `${data_type}_light`, { "_id*ObjectIDs": light_ids.slice(i, i + LIGHT_CHUNK_SIZE) });
        light_docs.push(...res.docs);
      }
      downloadAsExtendedJson(`${data_type}_backup_${stamp}.json`, full_docs);
      if (light_docs.length > 0) {
        downloadAsExtendedJson(`${data_type}_light_backup_${stamp}.json`, light_docs);
      }
    }
  };

  const deleteUnused = async () => {
    setConfirmDelete(false);
    setBackupError("");
    const targets = [...deletable];
    try {
      await downloadBackup(targets);
    } catch (ex: any) {
      console.error(ex);
      setDeleteStatus("");
      setBackupError(`Backup failed, so nothing was deleted: ${ex?.message ?? ex}`);
      return;
    }
    setDeleteStatus("");
    const failed: string[] = [];
    const deleted_ids: string[] = [];
    setDeleting({ done: 0, total: targets.length, failed });
    for (let i = 0; i < targets.length; ++i) {
      const { data_type, obj } = targets[i];
      try {
        const res = await api.deleteObject(data_type, obj);
        if (res?.body?.message?.includes("deleted!")) {
          deleted_ids.push(obj._id);
        } else {
          failed.push(obj.name);
        }
      } catch (ex) {
        console.error(ex);
        failed.push(obj.name);
      }
      if (!mounted.current) {
        return;
      }
      setDeleting({ done: i + 1, total: targets.length, failed: [...failed] });
    }
    setFeatures(prev => prev.filter(f => !deleted_ids.includes(f.obj._id)));
  };

  const renderDelete = () => {
    if (delete_status !== "") {
      return <>{ delete_status }</>;
    }
    if (deleting && delete_running) {
      return <>Deleting { deleting.done } / { deleting.total }...</>;
    }
    if (confirm_delete) {
      return (
        <>
          <div>
            Permanently delete { deletable.length } unused feature{ deletable.length === 1 ? "" : "s" }?
            Their full and _light documents will download as backup files first (one per collection, importable with Compass), and nothing is deleted if that fails.
            Characters store their own feature ids and weren&apos;t checked, so older characters may still point at some of these.
          </div>
          <DandDButtonBox name={`Download Backup, Then Delete ${deletable.length}`} onClick={deleteUnused} />
          <DandDButtonBox name="Cancel" onClick={() => setConfirmDelete(false)} />
        </>
      );
    }
    return (
      <>
        <DandDButtonBox name={`Delete All Unused (${deletable.length})`} disabled={deletable.length === 0 || busy || scan_failures.length > 0} onClick={() => setConfirmDelete(true)} />
        { backup_error !== "" && <div style={{ color: "red" }}>{ backup_error }</div> }
        { deleting &&
          <div>
            Deleted { deleting.total - deleting.failed.length } of { deleting.total }.
            { deleting.failed.length > 0 && ` Failed: ${deleting.failed.join(", ")}` }
          </div>
        }
      </>
    );
  };

  const bookList = (ids: string[]) => ids
    .map(id => book_names.get(id) ?? `Unknown (${id})`)
    .sort((a, b) => a.localeCompare(b))
    .join(", ") || "None";

  const toggle = (id: string) => setExpanded(prev => prev.includes(id) ? prev.filter(o => o !== id) : [...prev, id]);

  const renderLinks = (user_type: DataType, user_id: string) => {
    if (user_type !== "creature" && user_type !== "creature_gift") {
      return null;
    }
    const links = index.links.get(user_id) ?? [];
    if (links.length === 0) {
      return null;
    }
    return (
      <div style={{ paddingLeft: 24, fontSize: "0.85em", opacity: 0.8 }}>
        { links.map((l, i) => (
          <div key={i}>
            { l.kind === "creature" ? "Summon/Transform target of" : "Built-in gift of" }{" "}
            { typeName(l.from_type) }: <Link to={`/${l.from_type}/${l.from_id}`}>{ l.from_name }</Link>
            { l.path.length > 0 && ` (${l.path.join(" → ")})` }
          </div>
        ))}
      </div>
    );
  };

  return (
    <Grid container spacing={1} direction="row" className="padded">
      <Grid size={12} className="header centered">Feature Usage</Grid>
      <Grid size={12} className="centered">
        <div>
          Scanned { scanned_docs } documents across { scan_results.length } collections.{" "}
          <span className="clickable" style={{ textDecoration: "underline" }} onClick={() => setShowScanDetails(prev => !prev)}>
            { show_scan_details ? "Hide details" : "Show details" }
          </span>
        </div>
        { scan_failures.length > 0 &&
          <div style={{ color: "red" }}>
            These collections didn&apos;t fully load, so Save and Delete are disabled until a reload scans cleanly:{" "}
            { scan_failures.map(o => `${typeName(o.data_type)} (${o.error})`).join(", ") }
          </div>
        }
        { show_scan_details &&
          <div style={{ fontSize: "0.85em", opacity: 0.8 }}>
            { scan_results.map(o => `${typeName(o.data_type)}: ${o.loaded}${o.error ? ` (${o.error})` : ""}`).join(" · ") }
          </div>
        }
      </Grid>
      <Grid size={12} className="centered">
        { features.length } features, { unused_total } unused, { need_update_total } need source book updates{ unresolvable_total > 0 ? `, ${unresolvable_total} used but with no resolvable source book` : "" }. Showing { Math.min(limit, filtered.length) } of { filtered.length }.
      </Grid>
      <Grid size={{ xs: 12, md: 6 }} className="centered">
        { renderSave() }
      </Grid>
      <Grid size={{ xs: 12, md: 6 }} className="centered">
        { renderDelete() }
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <SelectStringBox name="Appears in Source Books" options={book_options} values={book_filter} multiple
          disabled={busy}
          onChange={(changed: string[]) => { setBookFilter(changed); setLimit(PAGE_SIZE); }}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <StringBox name="Filter by name or feature type" disabled={busy} value={filter} onChange={(value: string) => { setFilter(value); setLimit(PAGE_SIZE); }} />
      </Grid>
      <Grid size={{ xs: 6, md: 3 }}>
        <CheckBox name="Unused Only" disabled={busy} value={unused_only} onChange={(value: boolean) => { setUnusedOnly(value); setLimit(PAGE_SIZE); }} />
      </Grid>
      <Grid size={{ xs: 6, md: 3 }}>
        <CheckBox name="Needs Source Book Update" disabled={busy} value={needs_update_only} onChange={(value: boolean) => { setNeedsUpdateOnly(value); setLimit(PAGE_SIZE); }} />
      </Grid>
      <Grid size={{ xs: 6, md: 3 }}>
        <CheckBox name="Used, No Source Book" disabled={busy} value={no_book_only} onChange={(value: boolean) => { setNoBookOnly(value); setLimit(PAGE_SIZE); }} />
      </Grid>
      <Grid size={{ xs: 6, md: 3 }}>
        <CheckBox name="Include Creature Features" disabled={busy} value={show_creature_features} onChange={(value: boolean) => { setShowCreatureFeatures(value); setLimit(PAGE_SIZE); }} />
      </Grid>
      { filtered.slice(0, limit).map(({ data_type, obj }) => {
        const usages = index.usages.get(obj._id) ?? [];
        const open = expanded.includes(obj._id);
        console.log(obj);
        return (
          <Grid key={obj._id} size={12} style={{ borderBottom: "1px solid rgba(128,128,128,0.3)", paddingBottom: 4 }}>
            <div className={usages.length > 0 ? "clickable" : ""} onClick={() => usages.length > 0 && toggle(obj._id)}>
              { usages.length > 0 ? (open ? "▾ " : "▸ ") : "   " }
              <Link to={`/${data_type}/${obj._id}`} onClick={e => e.stopPropagation()}>{ obj.name || "(unnamed)" }</Link>
              <span style={{ opacity: 0.7 }}> — { obj.feature_type || "No type" }{ data_type === "creature_feature" ? " (Creature Feature)" : "" }</span>
              <strong style={{ float: "right" }}>
                { needsUpdate(obj) && <span style={{ color: "orange" }}>Source books out of date · </span> }
                { usages.length === 0 ? "Unused" : `${usages.length} use${usages.length === 1 ? "" : "s"}` }
              </strong>
            </div>
            { open && usages.map((u, i) => (
              <div key={i} style={{ paddingLeft: 24 }}>
                { typeName(u.user_type) }: <Link to={`/${u.user_type}/${u.user_id}`}>{ u.user_name }</Link>
                { describeUsagePath(u) !== "" && <span style={{ opacity: 0.7 }}> — { describeUsagePath(u) }</span> }
                <span style={{ opacity: 0.7, fontSize: "0.85em" }}>
                  {" "}[{ FEATURE_TYPES.includes(u.user_type) ?
                    `books come from its own uses: ${bookList(feature_books.get(u.user_id) ?? [])}` :
                    `books: ${bookList(u.source_book_ids)}` }]
                </span>
                { renderLinks(u.user_type, u.user_id) }
              </div>
            ))}
            { open && usages.length > 0 &&
              <div style={{ paddingLeft: 24, paddingTop: 4 }}>
                <strong>Source Books: </strong>
                { bookList(feature_books.get(obj._id) ?? []) }
              </div>
            }
            { open &&
              <div style={{ paddingLeft: 24 }}>
                <strong>Saved Source Books: </strong>
                { bookList(savedBooks(obj)) }
              </div>
            }
          </Grid>
        );
      })}
      { filtered.length > limit &&
        <Grid size={12} className="centered">
          <DandDButtonBox name="Show More" onClick={() => setLimit(prev => prev + PAGE_SIZE)} />
        </Grid>
      }
    </Grid>
  );
}
